import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '../lib/cn';

const FOCUSABLE = 'a[href], button:not([disabled]), textarea, input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal — centered card on tablet+, bottom-sheet on mobile.
 * ESC + click-outside close. Tab is trapped inside; focus restores to opener on close.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  // Latest onClose in a ref so the focus-trap effect depends only on `open` — an inline/
  // unstable onClose would otherwise re-run the trap each render and steal the caret.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    previousFocus.current = (document.activeElement as HTMLElement | null) ?? null;
    document.body.style.overflow = 'hidden';

    setTimeout(() => {
      const node = dialogRef.current;
      if (!node) return;
      const focusables = node.querySelectorAll<HTMLElement>(FOCUSABLE);
      (focusables[0] ?? node).focus();
    }, 10);

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (e.key === 'Tab') {
        const node = dialogRef.current;
        if (!node) return;
        const focusables = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE));
        if (focusables.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusables[0]!;
        const last = focusables[focusables.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      previousFocus.current?.focus?.();
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- onClose read via onCloseRef

  if (!open) return null;

  const widths = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-2xl',
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-zinc-950/40 backdrop-blur-[2px] animate-in fade-in"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        tabIndex={-1}
        className={cn(
          'relative w-full',
          widths[size],
          'rounded-t-xl sm:rounded-xl bg-white dark:bg-zinc-900 shadow-lg',
          'max-h-[90vh] overflow-y-auto',
          'border-t border-zinc-200 sm:border dark:border-zinc-700',
          'outline-none',
        )}
      >
        {title && (
          <div className="flex items-center justify-between gap-4 px-6 pt-6 pb-4 sticky top-0 bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-700 z-10">
            <h2 className="text-lg font-semibold text-zinc-950 dark:text-zinc-50">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 grid place-items-center rounded-md text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        <div className="px-6 py-5">{children}</div>
        {footer && (
          <div className="px-6 py-4 border-t border-zinc-200 dark:border-zinc-700 flex flex-wrap gap-3 justify-end sticky bottom-0 bg-white dark:bg-zinc-900">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
